import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTicketAlt,
  faCommentDots,
  faLock,
  faMobileAlt,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { validateInappropriateLanguage } from '../../../utils/profanitySanitizer';

/**
 * Modal adaptativo:
 * - Si tiene tickets vinculados: Editor estructurado con plantilla Meta Utilidad y simulador WhatsApp en vivo.
 * - Si NO tiene tickets: Confirmación limpia y directa sin simulador de WhatsApp con nota interna opcional.
 */
const TagCompleteModal = ({
  itemToComplete,
  isCompleting,
  isDark,
  onClose,
  onConfirm
}) => {
  const [resolutionMessage, setResolutionMessage] = useState('');

  const prefixText = 'Hola, respecto a tu reporte de soporte técnico te informamos lo siguiente:';
  const suffixText = 'Agradecemos tu paciencia y quedamos a tu disposición si tienes alguna duda adicional.';

  const totalTickets = itemToComplete?.iTotalTickets || 0;
  const hasTickets = totalTickets > 0;

  useEffect(() => {
    if (itemToComplete) {
      if (hasTickets) {
        setResolutionMessage(
          `La incidencia "${itemToComplete.vNombre}" ha sido resuelto con éxito.`
        );
      } else {
        setResolutionMessage('');
      }
    }
  }, [itemToComplete, hasTickets]);

  // Validación y sanitización de lenguaje inapropiado / groserías
  const profanityValidation = useMemo(() => {
    return validateInappropriateLanguage(resolutionMessage);
  }, [resolutionMessage]);

  if (!itemToComplete) return null;

  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.68)', zIndex: 1060 }}
      tabIndex="-1"
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: hasTickets ? '890px' : '540px', width: '94%' }}
      >
        <div
          className="modal-content border shadow-lg"
          style={{
            backgroundColor: isDark ? '#14202e' : '#ffffff',
            borderColor: isDark ? '#233142' : '#cbd5e1',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
        >
          {/* Cabecera del modal */}
          <div
            className="modal-header border-bottom px-4 py-3 d-flex align-items-center justify-content-between"
            style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
          >
            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-success"
                style={{
                  width: '38px',
                  height: '38px',
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5'
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.15rem' }} />
              </div>
              <div>
                <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '1rem' }}>
                  Resolver Incidencia
                </h6>
                <div className="small fw-semibold text-primary" style={{ fontSize: '0.86rem' }}>
                  "{itemToComplete.vNombre}"
                </div>
              </div>
            </div>

            {hasTickets ? (
              <div
                className="d-flex align-items-center rounded-pill border"
                style={{
                  backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                  borderColor: isDark ? '#233142' : '#e2e8f0',
                  padding: '6px 14px',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faTicketAlt} className="text-primary" style={{ fontSize: '0.85rem' }} />
                <span className="small fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '0.82rem' }}>
                  {totalTickets} ticket{totalTickets === 1 ? '' : 's'} vinculados
                </span>
              </div>
            ) : (
              <div
                className="d-flex align-items-center rounded-pill border"
                style={{
                  backgroundColor: isDark ? '#1a2636' : '#f8fafc',
                  borderColor: isDark ? '#293a4f' : '#e2e8f0',
                  padding: '5px 12px',
                  gap: '6px'
                }}
              >
                <FontAwesomeIcon icon={faTicketAlt} style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }} />
                <span className="small fw-semibold" style={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.78rem' }}>
                  Sin tickets
                </span>
              </div>
            )}
          </div>

          {/* Cuerpo del modal según si tiene tickets o no */}
          {hasTickets ? (
            /* CON TICKETS: 2 COLUMNAS (EDITOR + VISTA PREVIA WHATSAPP) */
            <div className="modal-body px-4 py-3">
              <div className="row g-3">
                {/* COLUMNA IZQUIERDA: EDITOR ESTRUCTURADO */}
                <div className="col-12 col-md-6 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span
                      className="small fw-bold d-flex align-items-center"
                      style={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: '0.84rem', gap: '8px' }}
                    >
                      <FontAwesomeIcon icon={faCommentDots} className="text-primary" />
                      <span>Redactar Notificación</span>
                    </span>
                    <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                      Plantilla Meta Utilidad
                    </span>
                  </div>

                  <div className="d-flex flex-column flex-grow-1" style={{ gap: '12px' }}>
                    {/* Prefijo fijo */}
                    <div
                      className="rounded-3 border"
                      style={{
                        backgroundColor: isDark ? '#0e1824' : '#f8fafc',
                        borderColor: isDark ? '#223247' : '#e2e8f0',
                        padding: '11px 14px'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span
                          className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                          style={{
                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#64748b',
                            fontSize: '0.68rem',
                            padding: '3px 9px',
                            gap: '6px'
                          }}
                        >
                          <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.65rem' }} />
                          <span>Inicio del mensaje (Fijo)</span>
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.68rem' }}>Plantilla oficial</span>
                      </div>
                      <div
                        style={{
                          color: isDark ? '#cbd5e1' : '#334155',
                          fontSize: '0.84rem',
                          lineHeight: '1.5'
                        }}
                      >
                        {prefixText}
                      </div>
                    </div>

                    {/* Alerta de lenguaje inapropiado */}
                    {!profanityValidation.isValid && (
                      <div
                        className="alert alert-danger d-flex align-items-center p-2 rounded-2 border mb-0"
                        style={{
                          fontSize: '0.74rem',
                          backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                          borderColor: isDark ? 'rgba(239, 68, 68, 0.35)' : '#fecaca',
                          color: isDark ? '#fca5a5' : '#b91c1c',
                          gap: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                        <span>
                          <strong>Lenguaje no permitido:</strong> Se detectó el término <code>"{profanityValidation.foundWord}"</code>. No se permite enviar palabras obscenas a los clientes.
                        </span>
                      </div>
                    )}

                    {/* Cuerpo editable */}
                    <div className="flex-grow-1 d-flex flex-column" style={{ gap: '6px' }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                          style={{
                            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                            color: isDark ? '#38bdf8' : '#0369a1',
                            fontSize: '0.70rem',
                            padding: '4px 9px',
                            gap: '6px'
                          }}
                        >
                          <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: '0.65rem' }} />
                          <span>Detalle de la resolución (editable)</span>
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {resolutionMessage.length} caracteres
                        </span>
                      </div>
                      <textarea
                        className={`form-control ${!profanityValidation.isValid ? 'is-invalid' : ''}`}
                        rows={4}
                        value={resolutionMessage}
                        onChange={e => setResolutionMessage(e.target.value)}
                        placeholder="Escribe la solución específica para los clientes..."
                        style={{
                          backgroundColor: isDark ? '#0f172a' : '#ffffff',
                          border: !profanityValidation.isValid
                            ? '1px solid #ef4444'
                            : `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                          borderRadius: '10px',
                          color: isDark ? '#f8fafc' : '#0f172a',
                          fontSize: '0.85rem',
                          lineHeight: '1.5',
                          padding: '11px 13px',
                          minHeight: '105px'
                        }}
                        disabled={isCompleting}
                      />
                    </div>

                    {/* Cierre fijo */}
                    <div
                      className="rounded-3 border"
                      style={{
                        backgroundColor: isDark ? '#0e1824' : '#f8fafc',
                        borderColor: isDark ? '#223247' : '#e2e8f0',
                        padding: '11px 14px'
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span
                          className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                          style={{
                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#e2e8f0',
                            color: isDark ? '#94a3b8' : '#64748b',
                            fontSize: '0.68rem',
                            padding: '3px 9px',
                            gap: '6px'
                          }}
                        >
                          <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.65rem' }} />
                          <span>Cierre del mensaje (Fijo)</span>
                        </span>
                        <span className="text-muted" style={{ fontSize: '0.68rem' }}>Plantilla oficial</span>
                      </div>
                      <div
                        style={{
                          color: isDark ? '#cbd5e1' : '#334155',
                          fontSize: '0.84rem',
                          lineHeight: '1.5'
                        }}
                      >
                        {suffixText}
                      </div>
                    </div>
                  </div>
                </div>

                {/* COLUMNA DERECHA: VISTA PREVIA SIEMPRE VISIBLE */}
                <div className="col-12 col-md-6 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span
                      className="small fw-bold d-flex align-items-center"
                      style={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: '0.84rem', gap: '8px' }}
                    >
                      <FontAwesomeIcon icon={faMobileAlt} className="text-success" />
                      <span>Vista Previa en WhatsApp</span>
                    </span>
                    <span
                      className="badge rounded-pill fw-semibold"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontSize: '0.70rem',
                        padding: '3px 8px'
                      }}
                    >
                      En Vivo
                    </span>
                  </div>

                  <div
                    className="p-3 rounded-3 border flex-grow-1 d-flex flex-column justify-content-center"
                    style={{
                      backgroundColor: isDark ? '#0b141a' : '#efeae2',
                      borderColor: isDark ? '#1e293b' : '#d1d7db',
                      backgroundImage: isDark
                        ? 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 0)'
                        : 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 0)',
                      backgroundSize: '16px 16px',
                      minHeight: '260px'
                    }}
                  >
                    {/* Burbuja WhatsApp */}
                    <div
                      className="p-3 rounded-3 shadow-sm position-relative"
                      style={{
                        maxWidth: '96%',
                        alignSelf: 'flex-start',
                        backgroundColor: isDark ? '#005c4b' : '#ffffff',
                        color: isDark ? '#e9edef' : '#111b21',
                        borderRadius: '8px 8px 8px 0px',
                        border: isDark ? '1px solid #005c4b' : '1px solid #e0e0e0',
                        fontSize: '0.83rem',
                        lineHeight: '1.45'
                      }}
                    >
                      {/* Encabezado remitente */}
                      <div
                        className="fw-bold mb-2"
                        style={{
                          fontSize: '0.73rem',
                          color: isDark ? '#25d366' : '#075e54'
                        }}
                      >
                        Soporte Akasia • WhatsApp
                      </div>

                      {/* Prefijo */}
                      <div className="mb-2" style={{ opacity: 0.95 }}>
                        {prefixText}
                      </div>

                      {/* Mensaje de resolución editable */}
                      <div className="mb-2 fw-semibold" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {resolutionMessage || '(Escribe el detalle de la resolución)'}
                      </div>

                      {/* Cierre */}
                      <div style={{ opacity: 0.95 }}>
                        {suffixText}
                      </div>

                      {/* Hora y Doble Check Azul */}
                      <div
                        className="d-flex justify-content-end align-items-center gap-1 mt-2"
                        style={{ fontSize: '0.66rem', color: isDark ? '#8696a0' : '#667781' }}
                      >
                        <span>{timeStr}</span>
                        <span style={{ color: '#53bdeb', fontWeight: 'bold' }}>✓✓</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 text-center">
                    <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                      📱 Así es exactamente como cada cliente vinculado recibirá el mensaje en su WhatsApp.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* SIN TICKETS: CONFIRMACIÓN DIRECTA Y LIMPIA */
            <div className="modal-body p-4 d-flex flex-column" style={{ gap: '18px' }}>
              <div
                className="rounded-3 border p-3 d-flex align-items-start"
                style={{
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
                  borderColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe',
                  gap: '12px'
                }}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ color: '#3b82f6', marginTop: '3px', fontSize: '1.1rem', flexShrink: 0 }}
                />
                <div>
                  <div className="fw-semibold" style={{ color: isDark ? '#93c5fd' : '#1e40af', fontSize: '0.88rem' }}>
                    Incidencia sin tickets vinculados
                  </div>
                  <p className="small mb-0 mt-1" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.83rem', lineHeight: '1.5' }}>
                    Esta incidencia no cuenta con tickets vinculados. Al resolverla, su estatus se actualizará a <strong>Resuelta</strong> en el sistema sin emitir mensajes externos por WhatsApp a clientes.
                  </p>
                </div>
              </div>

              <div className="d-flex flex-column" style={{ gap: '6px' }}>
                <label
                  className="small fw-semibold d-flex align-items-center justify-content-between"
                  style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.82rem' }}
                >
                  <span>Nota o motivo de resolución (opcional):</span>
                  <span className="text-muted" style={{ fontSize: '0.72rem' }}>Interno</span>
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={resolutionMessage}
                  onChange={e => setResolutionMessage(e.target.value)}
                  placeholder="Escribe alguna observación o detalle de la resolución (opcional)..."
                  style={{
                    backgroundColor: isDark ? '#0f172a' : '#ffffff',
                    border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                    borderRadius: '10px',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '0.85rem',
                    lineHeight: '1.5',
                    padding: '11px 13px'
                  }}
                  disabled={isCompleting}
                />
              </div>
            </div>
          )}

          {/* Footer del modal */}
          <div
            className="modal-footer border-top px-4 py-3 d-flex align-items-center justify-content-between"
            style={{ borderColor: isDark ? '#233142' : '#e2e8f0', gap: '12px' }}
          >
            <div className="small text-muted" style={{ fontSize: '0.78rem' }}>
              {hasTickets ? (
                <>
                  Al confirmar, se enviará la notificación de resolución a los <strong>{totalTickets}</strong> ticket(s) vinculados.
                </>
              ) : (
                <>
                  Se marcará el estado de la incidencia como <strong>Resuelta</strong>.
                </>
              )}
            </div>

            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill shadow-none"
                onClick={onClose}
                disabled={isCompleting}
                style={{ fontSize: '0.84rem', padding: '7px 16px' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-success rounded-pill shadow-none fw-semibold d-flex align-items-center"
                onClick={() => {
                  if (hasTickets && (!profanityValidation.isValid || !resolutionMessage.trim())) return;
                  onConfirm(resolutionMessage.trim());
                }}
                disabled={isCompleting || (hasTickets && (!resolutionMessage.trim() || !profanityValidation.isValid))}
                style={{
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                  fontSize: '0.84rem',
                  padding: '7px 20px',
                  gap: '8px'
                }}
              >
                {isCompleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span>{hasTickets ? 'Resolviendo y notificando...' : 'Resolviendo...'}</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Confirmar y Resolver</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagCompleteModal;
